/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Pure helpers for the buyer
 * board: line quantities, approval states, cart totals, order readiness,
 * nudges and the PO approval tiers. No React, no store access.
 */
import { getContract } from '@/demo/procurement/data/contracts';
import {
  daysFromNow,
  daysUntil,
  parseIsoDate,
} from '@/demo/procurement/data/dates';
import { getSupplier } from '@/demo/procurement/data/suppliers';
import { getPerson, getSite } from '@/demo/procurement/data/company';
import type { ContractId } from '@/demo/procurement/data/types';
import {
  displayQty,
  fmtMoney,
  fmtNumber,
  fmtQtyWithUnit,
  priceUnit,
} from '@/demo/procurement/data/format';
import {
  BUYERS,
  CARTS,
  SEED_LINES,
  TRANSFER_TRANSIT_DAYS,
  getItem,
} from './buyer-board-data';
import { siteInMode, type BusinessMode } from './business-mode';
import type {
  ApprovalAction,
  ApprovalState,
  ApprovalTier,
  BoardCart,
  BoardItem,
  BoardLine,
  BuyerId,
  DemandRef,
  ForwardBuyReason,
  LeadTimeStats,
  LineMetrics,
  LineType,
  OrderReadiness,
  PoGate,
  ReadinessState,
  ReleasedPo,
  ShipToId,
  ThresholdCheck,
} from './buyer-board-types';

// ---------------------------------------------------------------------------
// Quantities
// ---------------------------------------------------------------------------

export function roundUpToPack(qty: number, pack: number): number {
  if (qty <= 0) return 0;
  return Math.ceil(qty / pack - 1e-9) * pack;
}

function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

export function netRequirement(line: BoardLine): number {
  return Math.max(
    0,
    line.demand + line.safetyStock - line.onHand - line.onOrder,
  );
}

/**
 * The engine's calculation and what the line will order. Forward-buy lines
 * carry no calculation: their quantity is the uplift the buyer added.
 */
export function computeLineMetrics(
  line: BoardLine,
  item: BoardItem,
): LineMetrics {
  const weeksOfCover =
    line.weeklyUsage > 0
      ? Math.round((line.onHand / line.weeklyUsage) * 10) / 10
      : 0;
  const net = netRequirement(line);
  if (line.forwardBuy) {
    const qty = line.forwardBuy.addedQty;
    return {
      calcQty: 0,
      qty,
      buyQty: qty,
      extCost: roundCents(qty * line.unitCost),
      weeksOfCover,
      netRequirement: net,
    };
  }
  let calcQty: number;
  if (line.plan === 'transfer') {
    calcQty = line.transferQty;
  } else {
    const buyNeed = Math.max(0, net - line.transferQty);
    calcQty =
      buyNeed > 0
        ? Math.max(roundUpToPack(buyNeed, item.packQty), item.moq)
        : 0;
  }
  const qty = line.qtyOverride ?? calcQty;
  const buyQty = line.plan === 'transfer' ? 0 : qty;
  return {
    calcQty,
    qty,
    buyQty,
    extCost: roundCents(buyQty * line.unitCost),
    weeksOfCover,
    netRequirement: net,
  };
}

/** Whole days from today until a buy leg arrives (observed lead time). */
export function buyArrivalInDays(lead: LeadTimeStats): number {
  return Math.round(lead.observedWeeks * 7);
}

export function transferArrivalInDays(from: ShipToId, to: ShipToId): number {
  return TRANSFER_TRANSIT_DAYS[from][to];
}

/** Days from today until the line's material arrives. */
export function lineArrivalInDays(line: BoardLine, cart: BoardCart): number {
  if (line.plan === 'transfer' && line.transferFrom !== null) {
    return transferArrivalInDays(line.transferFrom, line.shipToId);
  }
  return buyArrivalInDays(cart.leadTime);
}

// ---------------------------------------------------------------------------
// Approval states
// ---------------------------------------------------------------------------

export function isInCart(state: ApprovalState): boolean {
  return state === 'approved' || state === 'auto_approved';
}

/** The rule behind the Auto-approve toggle. */
export function isAutoApproveEligible(
  line: BoardLine,
  item: BoardItem,
  metrics: LineMetrics,
): boolean {
  if (line.forwardBuy) return false;
  if (item.pop !== 'A' && item.pop !== 'B') return false;
  if (item.consumptionMonths < 12) return false;
  if (item.stockedSites < 2) return false;
  if (metrics.calcQty <= 0) return false;
  return Math.abs(metrics.qty - metrics.calcQty) / metrics.calcQty <= 0.1;
}

export function resolveState(
  line: BoardLine,
  item: BoardItem,
  metrics: LineMetrics,
  overrides: Readonly<Record<string, ApprovalState>>,
  autoApprove: boolean,
): ApprovalState {
  const override = overrides[line.id];
  if (override !== undefined) return override;
  if (
    autoApprove &&
    line.state === 'unapproved' &&
    isAutoApproveEligible(line, item, metrics)
  ) {
    return 'auto_approved';
  }
  return line.state;
}

/** A hold keeps the line off the order whatever the buyer sets. */
export function isApprovalBlocked(line: BoardLine, cart: BoardCart): boolean {
  return cart.hold !== null || line.state === 'credit_hold';
}

/**
 * Actions open to a line from its state badge, in menu order, never including
 * its current state. A rejected line can only be restored, and a line under a
 * hold cannot be approved.
 */
export function approvalActions(
  state: ApprovalState,
  blocked: boolean,
): ApprovalAction[] {
  if (state === 'rejected') return ['restore'];
  if (state === 'credit_hold') return ['rejected'];
  const all: ApprovalAction[] = [
    'approved',
    'unapproved',
    'trouble',
    'rejected',
  ];
  const current: ApprovalAction | null =
    state === 'auto_approved'
      ? 'approved'
      : state === 'approved' || state === 'unapproved' || state === 'trouble'
        ? state
        : null;
  return all.filter(
    (action) => action !== current && !(blocked && action === 'approved'),
  );
}

/** What the state column shows: short enough for a narrow column. */
export const APPROVAL_STATE_LABEL: Record<ApprovalState, string> = {
  approved: 'Approved',
  auto_approved: 'Auto',
  unapproved: 'Pending',
  trouble: 'On hold',
  credit_hold: 'Payment hold',
  rejected: 'Rejected',
};

export const APPROVAL_STATE_NAME: Record<ApprovalState, string> = {
  approved: 'Approved by the buyer',
  auto_approved: 'Approved automatically',
  unapproved: 'Waiting for the buyer to approve',
  trouble: 'On hold while the buyer looks into a problem',
  credit_hold: 'Held because of a payment problem',
  rejected: 'Rejected by the buyer',
};

export const APPROVAL_ACTION_NAME: Record<ApprovalAction, string> = {
  approved: 'Approve',
  unapproved: 'Move back to pending',
  trouble: 'Put on hold',
  rejected: 'Reject',
  restore: 'Restore',
};

// ---------------------------------------------------------------------------
// Resolved board
// ---------------------------------------------------------------------------

export interface ResolvedLine {
  line: BoardLine;
  item: BoardItem;
  metrics: LineMetrics;
  state: ApprovalState;
  /** PO the line was released to, when released. */
  releasedPo: string | null;
  arrivalInDays: number;
}

export interface CartTotals {
  backorder: number;
  potf: number;
  stock: number;
  /** Value of in-cart (approved) buy lines: what the PO would be. */
  poValue: number;
  /** Unreleased, non-rejected lines. */
  lineCount: number;
  inCartCount: number;
  buyLineCount: number;
  belowMoqCount: number;
  releasedCount: number;
  oldestNeedInDays: number | null;
}

export interface ResolvedCart {
  cart: BoardCart;
  lines: ResolvedLine[];
  totals: CartTotals;
  readiness: OrderReadiness;
}

export interface BoardStateSlice {
  autoApprove: boolean;
  stateOverrides: Readonly<Record<string, ApprovalState>>;
  /** Lines the buyer or an AI note added after the engine ran. */
  addedLines: readonly BoardLine[];
  released: Readonly<Record<string, string>>;
}

export function computeCartTotals(lines: readonly ResolvedLine[]): CartTotals {
  const totals: CartTotals = {
    backorder: 0,
    potf: 0,
    stock: 0,
    poValue: 0,
    lineCount: 0,
    inCartCount: 0,
    buyLineCount: 0,
    belowMoqCount: 0,
    releasedCount: 0,
    oldestNeedInDays: null,
  };
  for (const entry of lines) {
    if (entry.releasedPo !== null) {
      totals.releasedCount += 1;
      continue;
    }
    if (entry.state === 'rejected') continue;
    totals.lineCount += 1;
    totals[entry.line.type] += entry.metrics.extCost;
    const need = entry.line.needInDays;
    if (totals.oldestNeedInDays === null || need < totals.oldestNeedInDays) {
      totals.oldestNeedInDays = need;
    }
    if (isInCart(entry.state)) {
      totals.inCartCount += 1;
      totals.poValue += entry.metrics.extCost;
      if (entry.metrics.buyQty > 0) {
        totals.buyLineCount += 1;
        if (entry.metrics.buyQty < entry.item.moq) totals.belowMoqCount += 1;
      }
    }
  }
  totals.backorder = roundCents(totals.backorder);
  totals.potf = roundCents(totals.potf);
  totals.stock = roundCents(totals.stock);
  totals.poValue = roundCents(totals.poValue);
  return totals;
}

function threshold(total: number, amount: number): ThresholdCheck {
  const gap = roundCents(Math.max(0, amount - total));
  return { amount, met: gap === 0, gap };
}

export function evaluateReadiness(
  cart: BoardCart,
  totals: CartTotals,
): OrderReadiness {
  const total = totals.poValue;
  const empty = totals.inCartCount === 0;
  const mov = cart.mov === null ? null : threshold(total, cart.mov);
  const freight =
    cart.freight.kind === 'threshold'
      ? threshold(total, cart.freight.amount)
      : null;
  const moqMet = totals.belowMoqCount === 0;
  const hold = cart.hold === null ? null : cart.hold.reason;
  let state: ReadinessState = 'ready';
  if (hold !== null || empty || !moqMet || (mov !== null && !mov.met)) {
    state = 'not_ready';
  } else if (freight !== null && !freight.met) {
    state = 'warning';
  }
  return {
    state,
    total,
    empty,
    mov,
    moq: {
      met: moqMet,
      belowCount: totals.belowMoqCount,
      lineCount: totals.buyLineCount,
    },
    freight,
    freightIncluded:
      cart.freight.kind === 'included'
        ? {
            incoterm: cart.freight.incoterm,
            clauseSection: cart.freight.clauseSection,
          }
        : null,
    hold,
  };
}

/** One line for the hover title: the state and its main reason. */
export function readinessSummary(readiness: OrderReadiness): string {
  if (readiness.hold !== null) {
    return `Not ready: ${readiness.hold}`;
  }
  if (readiness.empty) return 'Not ready: no approved lines in this order yet';
  if (!readiness.moq.met) {
    return readiness.moq.belowCount === 1
      ? "Not ready: 1 line is below the supplier's minimum quantity"
      : `Not ready: ${readiness.moq.belowCount} lines are below the supplier's minimum quantity`;
  }
  if (readiness.mov !== null && !readiness.mov.met) {
    return `Not ready: ${fmtMoney(readiness.mov.gap)} under the ${fmtMoney(readiness.mov.amount)} minimum order value`;
  }
  if (readiness.freight !== null && !readiness.freight.met) {
    return `Warning: ${fmtMoney(readiness.freight.gap)} short of the ${fmtMoney(readiness.freight.amount)} needed for free freight`;
  }
  return 'Ready to order';
}

export function readinessLabel(readiness: OrderReadiness): string {
  if (readiness.state === 'ready') return 'Ready';
  if (readiness.state === 'not_ready') return 'Not ready';
  return 'Freight not free';
}

export function resolveCart(
  cart: BoardCart,
  state: BoardStateSlice,
): ResolvedCart {
  const lines: ResolvedLine[] = [];
  const all = [
    ...SEED_LINES.filter((l) => l.cartId === cart.id),
    ...state.addedLines.filter((l) => l.cartId === cart.id),
  ];
  for (const line of all) {
    const item = getItem(line.itemCode);
    if (item === undefined) continue;
    const metrics = computeLineMetrics(line, item);
    lines.push({
      line,
      item,
      metrics,
      state: resolveState(
        line,
        item,
        metrics,
        state.stateOverrides,
        state.autoApprove,
      ),
      releasedPo: state.released[line.id] ?? null,
      arrivalInDays: lineArrivalInDays(line, cart),
    });
  }
  const totals = computeCartTotals(lines);
  return { cart, lines, totals, readiness: evaluateReadiness(cart, totals) };
}

export function resolveBoard(state: BoardStateSlice): ResolvedCart[] {
  return CARTS.map((cart) => resolveCart(cart, state));
}

// ---------------------------------------------------------------------------
// Filters, grouping and sorting
// ---------------------------------------------------------------------------

export interface BoardFilters {
  shipTos: ShipToId[];
  buyer: BuyerId | 'all';
  lineTypes: LineType[];
  readiness: ReadinessState | 'any';
  showRejected: boolean;
}

export const DEFAULT_FILTERS: BoardFilters = {
  shipTos: [],
  buyer: 'all',
  lineTypes: [],
  readiness: 'any',
  showRejected: false,
};

export function hasActiveFilters(filters: BoardFilters): boolean {
  return (
    filters.shipTos.length > 0 ||
    filters.buyer !== 'all' ||
    filters.lineTypes.length > 0 ||
    filters.readiness !== 'any' ||
    filters.showRejected
  );
}

/** Lines the detail pane shows for a cart under the filters. */
export function visibleLines(
  cart: ResolvedCart,
  filters: BoardFilters,
): ResolvedLine[] {
  return cart.lines.filter((entry) => {
    if (entry.releasedPo !== null) return false;
    if (entry.state === 'rejected' && !filters.showRejected) return false;
    return (
      filters.lineTypes.length === 0 ||
      filters.lineTypes.includes(entry.line.type)
    );
  });
}

export function releasedLines(cart: ResolvedCart): ResolvedLine[] {
  return cart.lines.filter((entry) => entry.releasedPo !== null);
}

export function filterCarts(
  carts: readonly ResolvedCart[],
  filters: BoardFilters,
): ResolvedCart[] {
  return carts.filter((entry) => {
    if (
      filters.shipTos.length > 0 &&
      !filters.shipTos.includes(entry.cart.shipToId)
    ) {
      return false;
    }
    if (filters.buyer !== 'all' && entry.cart.buyerId !== filters.buyer) {
      return false;
    }
    if (
      filters.readiness !== 'any' &&
      entry.readiness.state !== filters.readiness
    ) {
      return false;
    }
    const lines = visibleLines(entry, filters);
    // A cart whose lines are all released still shows, as Released.
    if (lines.length === 0 && releasedLines(entry).length === 0) return false;
    return true;
  });
}

/** The carts on the page in a business mode: by the kind of site they deliver to. */
export function cartsForMode(
  carts: readonly ResolvedCart[],
  mode: BusinessMode,
): ResolvedCart[] {
  return carts.filter((entry) => siteInMode(entry.cart.shipToId, mode));
}

export type CartSortKey =
  | 'default'
  | 'supplier'
  | 'shipTo'
  | 'buyer'
  | 'backorder'
  | 'potf'
  | 'stock'
  | 'lines'
  | 'value'
  | 'oldest';

export interface CartSort {
  key: CartSortKey;
  direction: 'asc' | 'desc';
}

function oldestKey(cart: ResolvedCart): number {
  return cart.totals.oldestNeedInDays ?? Number.MAX_SAFE_INTEGER;
}

/** Default order: carts with back orders first, then oldest need, then value. */
function compareDefault(a: ResolvedCart, b: ResolvedCart): number {
  const backorderA = a.totals.backorder > 0 ? 1 : 0;
  const backorderB = b.totals.backorder > 0 ? 1 : 0;
  if (backorderA !== backorderB) return backorderB - backorderA;
  const oldest = oldestKey(a) - oldestKey(b);
  if (oldest !== 0) return oldest;
  return b.totals.poValue - a.totals.poValue;
}

function sortValue(cart: ResolvedCart, key: CartSortKey): number | string {
  switch (key) {
    case 'supplier':
      return getSupplier(cart.cart.supplierId).shortName;
    case 'shipTo':
      return cart.cart.shipToId;
    case 'buyer':
      return BUYERS[cart.cart.buyerId].name;
    case 'backorder':
      return cart.totals.backorder;
    case 'potf':
      return cart.totals.potf;
    case 'stock':
      return cart.totals.stock;
    case 'lines':
      return cart.totals.lineCount;
    case 'value':
      return cart.totals.poValue;
    case 'oldest':
      return oldestKey(cart);
    case 'default':
      return 0;
  }
}

export function sortCarts(
  carts: readonly ResolvedCart[],
  sort: CartSort,
): ResolvedCart[] {
  const copy = [...carts];
  if (sort.key === 'default') return copy.sort(compareDefault);
  const dir = sort.direction === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    if (typeof va === 'string' && typeof vb === 'string') {
      return va.localeCompare(vb) * dir;
    }
    if (typeof va === 'number' && typeof vb === 'number') {
      return (va - vb) * dir;
    }
    return 0;
  });
}

export const LINE_TYPE_ORDER: LineType[] = ['backorder', 'potf', 'stock'];

export const LINE_TYPE_LABEL: Record<LineType, string> = {
  backorder: 'Back orders',
  potf: 'Special orders',
  stock: 'Stock',
};

/** One line of help for each group, shown as a tooltip. */
export const LINE_TYPE_HELP: Record<LineType, string> = {
  backorder: 'Items customers have ordered that we are short on',
  potf: 'Non-stock items bought for one customer order',
  stock: 'Refills that keep stocked items at their target level',
};

export function groupLines(
  lines: readonly ResolvedLine[],
): Record<LineType, ResolvedLine[]> {
  const groups: Record<LineType, ResolvedLine[]> = {
    backorder: [],
    potf: [],
    stock: [],
  };
  for (const entry of lines) groups[entry.line.type].push(entry);
  return groups;
}

export function groupSubtotal(lines: readonly ResolvedLine[]): number {
  return roundCents(
    lines.reduce(
      (sum, entry) =>
        entry.state === 'rejected' ? sum : sum + entry.metrics.extCost,
      0,
    ),
  );
}

// ---------------------------------------------------------------------------
// Nudges
// ---------------------------------------------------------------------------

/** The largest in-cart buy line of the cart, the one a forward buy extends. */
export function primaryBuyLine(cart: ResolvedCart): ResolvedLine | null {
  let best: ResolvedLine | null = null;
  for (const entry of cart.lines) {
    if (entry.releasedPo !== null || !isInCart(entry.state)) continue;
    if (entry.line.forwardBuy || entry.metrics.buyQty <= 0) continue;
    if (best === null || entry.metrics.extCost > best.metrics.extCost) {
      best = entry;
    }
  }
  return best;
}

export interface ThresholdNudge {
  constraint: 'mov' | 'freight';
  thresholdAmount: number;
  gap: number;
  itemCode: string;
  uom: string;
  addQty: number;
  addCost: number;
  newTotal: number;
  coverageDays: number;
  baseLineId: string;
}

/**
 * Quantity to add to the biggest buy line to clear the binding minimum: the
 * higher of the minimum order value and the prepaid-freight minimum, with the
 * minimum order value winning a tie.
 */
export function thresholdNudge(cart: ResolvedCart): ThresholdNudge | null {
  if (cart.cart.hold !== null) return null;
  const mov = cart.cart.mov ?? 0;
  const freight =
    cart.cart.freight.kind === 'threshold' ? cart.cart.freight.amount : 0;
  const amount = Math.max(mov, freight);
  if (amount <= 0) return null;
  const total = cart.totals.poValue;
  const gap = roundCents(amount - total);
  if (gap <= 0) return null;
  const base = primaryBuyLine(cart);
  if (base === null) return null;
  const addQty = roundUpToPack(gap / base.line.unitCost, base.item.packQty);
  if (addQty <= 0) return null;
  const addCost = roundCents(addQty * base.line.unitCost);
  return {
    constraint: mov >= freight ? 'mov' : 'freight',
    thresholdAmount: amount,
    gap,
    itemCode: base.item.code,
    uom: base.item.uom,
    addQty,
    addCost,
    newTotal: roundCents(total + addCost),
    coverageDays:
      base.line.weeklyUsage > 0
        ? Math.round((addQty / base.line.weeklyUsage) * 7)
        : 0,
    baseLineId: base.line.id,
  };
}

export interface PriceBreakNudge {
  itemCode: string;
  uom: string;
  addQty: number;
  tier: number;
  tierPrice: number;
  currentPrice: number;
  savings: number;
  addCost: number;
  coverageDays: number;
  baseLineId: string;
  ladder: { tier: number; label: string; price: number }[];
}

/** Saving on the added quantity, bought at the deeper tier instead of today's price. */
export function priceBreakNudge(cart: ResolvedCart): PriceBreakNudge | null {
  const spec = cart.cart.priceBreak;
  if (spec === null) return null;
  const base = cart.lines.find(
    (entry) =>
      entry.releasedPo === null &&
      entry.line.itemCode === spec.itemCode &&
      !entry.line.forwardBuy,
  );
  if (base === undefined) return null;
  const savings = roundCents(
    spec.addQty * (base.line.unitCost - spec.tierPrice),
  );
  if (savings <= 0) return null;
  return {
    itemCode: spec.itemCode,
    uom: base.item.uom,
    addQty: spec.addQty,
    tier: spec.tier,
    tierPrice: spec.tierPrice,
    currentPrice: base.line.unitCost,
    savings,
    addCost: roundCents(spec.addQty * spec.tierPrice),
    coverageDays:
      base.line.weeklyUsage > 0
        ? Math.round((spec.addQty / base.line.weeklyUsage) * 7)
        : 0,
    baseLineId: base.line.id,
    ladder: spec.ladder,
  };
}

export const BUY_FORWARD_WEEKS = 6;

export interface ContractExpiryNudge {
  contractId: ContractId;
  endDate: Date;
  daysLeft: number;
  weeks: number;
  itemCode: string;
  uom: string;
  unitCost: number;
  addQty: number;
  addCost: number;
  coverageDays: number;
  baseLineId: string;
}

/** Buy-forward ahead of a contract that ends before the next price is set. */
export function contractExpiryNudge(
  cart: ResolvedCart,
): ContractExpiryNudge | null {
  if (cart.cart.contractId === null) return null;
  const contract = getContract(cart.cart.contractId);
  if (contract.status !== 'in_renewal' && contract.status !== 'expiring') {
    return null;
  }
  const endDate = parseIsoDate(contract.endDate);
  const daysLeft = daysUntil(endDate);
  if (daysLeft < 0 || daysLeft > 120) return null;
  const base = cart.lines.find(
    (entry) =>
      entry.releasedPo === null &&
      !entry.line.forwardBuy &&
      entry.line.costSource.kind === 'contract' &&
      entry.line.costSource.contractId === cart.cart.contractId,
  );
  if (base === undefined) return null;
  const addQty = roundUpToPack(
    BUY_FORWARD_WEEKS * base.line.weeklyUsage,
    base.item.packQty,
  );
  return {
    contractId: contract.id,
    endDate,
    daysLeft,
    weeks: BUY_FORWARD_WEEKS,
    itemCode: base.item.code,
    uom: base.item.uom,
    unitCost: base.line.unitCost,
    addQty,
    addCost: roundCents(addQty * base.line.unitCost),
    coverageDays: BUY_FORWARD_WEEKS * 7,
    baseLineId: base.line.id,
  };
}

export function forwardBuyLineId(
  cartId: string,
  reason: ForwardBuyReason,
): string {
  return `${cartId}-FB-${reason}`;
}

interface ForwardBuyInput {
  base: BoardLine;
  reason: ForwardBuyReason;
  addedQty: number;
  coverageDays: number;
  unitCost: number;
  costSource: BoardLine['costSource'];
}

export function makeForwardBuyLine(input: ForwardBuyInput): BoardLine {
  const { base } = input;
  return {
    id: forwardBuyLineId(base.cartId, input.reason),
    cartId: base.cartId,
    type: 'stock',
    state: 'approved',
    holdNote: null,
    holdRef: null,
    shipToId: base.shipToId,
    itemCode: base.itemCode,
    demandRef: null,
    needInDays: base.needInDays,
    demand: 0,
    onHand: base.onHand,
    onOrder: 0,
    safetyStock: 0,
    weeklyUsage: base.weeklyUsage,
    qtyOverride: input.addedQty,
    plan: 'buy',
    transferQty: 0,
    transferFrom: null,
    trigger: 'forward_buy',
    priority: 'fill',
    unitCost: input.unitCost,
    listPrice: input.base.listPrice,
    costSource: input.costSource,
    why: [],
    forwardBuy: {
      reason: input.reason,
      addedQty: input.addedQty,
      coverageDays: input.coverageDays,
    },
    origin: 'added',
  };
}

export const FORWARD_BUY_REASON_LABEL: Record<ForwardBuyReason, string> = {
  mov: 'Clears the order minimum',
  price_break: 'Reaches the volume price',
  contract_expiry: 'Buy ahead before contract renewal',
};

// ---------------------------------------------------------------------------
// PO approval tiers
// ---------------------------------------------------------------------------

export const APPROVAL_TIERS: ApprovalTier[] = [
  { id: 'buyer', role: 'Buyer', limit: 50_000, approverId: null },
  {
    id: 'category-manager',
    role: 'Category manager',
    limit: 250_000,
    approverId: 'sarah-chen',
  },
  {
    id: 'director',
    role: 'Director',
    limit: 1_000_000,
    approverId: 'daniel-reyes',
  },
  {
    id: 'vp',
    role: 'VP Supply Chain',
    limit: Number.POSITIVE_INFINITY,
    approverId: 'marcus-hale',
  },
];

/** The lowest tier whose limit covers the PO value. */
export function requiredTier(total: number): ApprovalTier {
  const tier = APPROVAL_TIERS.find((candidate) => total <= candidate.limit);
  return tier ?? APPROVAL_TIERS[APPROVAL_TIERS.length - 1];
}

export function tierLimitLabel(tier: ApprovalTier): string {
  if (!Number.isFinite(tier.limit)) return 'Over $1M';
  return `Up to ${fmtMoney(tier.limit)}`;
}

export function tierApproverName(tier: ApprovalTier, buyerId: BuyerId): string {
  if (tier.approverId === null) return BUYERS[buyerId].name;
  return getPerson(tier.approverId).name;
}

/** Gates a new PO starts with: the buyer's own limit auto-approves, else one named approver. */
export function initialGates(total: number, buyerId: BuyerId): PoGate[] {
  const tier = requiredTier(total);
  if (tier.id === 'buyer') {
    return [
      {
        tierId: tier.id,
        role: tier.role,
        approverName: tierApproverName(tier, buyerId),
        status: 'Approved',
        viaEscalation: false,
        note: 'Auto-approved within the buyer limit',
        decidedAt: null,
      },
    ];
  }
  return [
    {
      tierId: tier.id,
      role: tier.role,
      approverName: tierApproverName(tier, buyerId),
      status: 'Pending',
      viaEscalation: false,
      note: `Waiting on ${tierApproverName(tier, buyerId)}`,
      decidedAt: null,
    },
  ];
}

export function nextTier(tierId: ApprovalTier['id']): ApprovalTier | null {
  const index = APPROVAL_TIERS.findIndex((tier) => tier.id === tierId);
  return APPROVAL_TIERS[index + 1] ?? null;
}

export function pendingGate(gates: readonly PoGate[]): PoGate | null {
  return (
    gates.find(
      (gate) => gate.status === 'Pending' || gate.status === 'Escalated',
    ) ?? null
  );
}

// ---------------------------------------------------------------------------
// Line detail: forecast vs usage and lead time
// ---------------------------------------------------------------------------

function hashString(value: string): number {
  let hash = 17;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 100_003;
  }
  return hash;
}

export interface SeriesPoint {
  label: string;
  actual: number | null;
  forecast: number;
}

const PAST_WEEKS = 8;
const FUTURE_WEEKS = 8;

/** Weekly usage for the last 8 weeks and the forecast for the next 8. */
export function lineSeries(line: BoardLine): SeriesPoint[] {
  const seed = hashString(line.id);
  const trend =
    line.type === 'backorder' ? 1.06 : line.type === 'potf' ? 1.03 : 1;
  const points: SeriesPoint[] = [];
  const total = PAST_WEEKS + FUTURE_WEEKS;
  for (let i = 0; i < total; i += 1) {
    const wobble = (((seed + i * 7919) % 97) / 97 - 0.5) * 0.16;
    const isPast = i < PAST_WEEKS;
    const weekOffset = i - PAST_WEEKS;
    const forecastWobble = (((seed + i * 4513) % 89) / 89 - 0.5) * 0.06;
    points.push({
      label: isPast ? `W${weekOffset}` : `W+${weekOffset + 1}`,
      actual: isPast ? Math.round(line.weeklyUsage * (1 + wobble)) : null,
      forecast: Math.round(
        line.weeklyUsage *
          (isPast ? 1 + forecastWobble / 2 : trend + forecastWobble),
      ),
    });
  }
  return points;
}

export interface LeadTimeRung {
  label: string;
  weeks: number;
  detail: string;
  picked: boolean;
}

export function leadTimeRungs(
  cart: BoardCart,
  shipToId: ShipToId,
): LeadTimeRung[] {
  const lead = cart.leadTime;
  const committedLabel =
    lead.clauseSection !== null
      ? `Agreement §${lead.clauseSection}`
      : 'Quoted by supplier';
  return [
    {
      label: `This item at ${getSite(shipToId).name}`,
      weeks: lead.observedWeeks,
      detail: `${lead.receipts} deliveries`,
      picked: true,
    },
    {
      label: 'This item across warehouses',
      weeks: Math.round((lead.observedWeeks - 0.2) * 10) / 10,
      detail: `${lead.receipts * 3} deliveries`,
      picked: false,
    },
    {
      label: committedLabel,
      weeks: lead.committedWeeks,
      detail: 'Not used in planning',
      picked: false,
    },
  ];
}

export function leadTimeSentence(cart: BoardCart): string {
  const lead = cart.leadTime;
  const source =
    lead.clauseSection !== null
      ? `${lead.committedWeeks} weeks promised in agreement §${lead.clauseSection}`
      : `${lead.committedWeeks} weeks quoted by the supplier`;
  return `Last ${lead.receipts} deliveries averaged ${lead.observedWeeks.toFixed(1)} weeks vs ${source}`;
}

/** 'Customer order 48310 · Acme Industrial' or 'Panel build · PPC-MCC-480 × 22 in Nov'; null when the line has no demand reference. */
export function demandRefLabel(line: BoardLine): string | null {
  return formatDemandRef(line.demandRef, line.needInDays);
}

export function formatDemandRef(
  ref: DemandRef | null,
  needInDays: number,
): string | null {
  if (ref === null) return null;
  if (ref.kind === 'so')
    return `Customer order ${ref.orderNo} · ${ref.customer}`;
  const month = daysFromNow(needInDays).toLocaleDateString('en-US', {
    month: 'short',
  });
  return `Panel build · ${ref.finishedGood} × ${ref.builds} in ${month}`;
}

/** The demand behind a line, written to sit inside a sentence. */
function demandRefPhrase(ref: DemandRef, needInDays: number): string {
  if (ref.kind === 'so') {
    return `Customer order ${ref.orderNo} (${ref.customer})`;
  }
  const month = daysFromNow(needInDays).toLocaleDateString('en-US', {
    month: 'short',
  });
  return `Building ${ref.finishedGood} × ${ref.builds} in ${month}`;
}

/** Why the engine suggested the line, as short sentences. */
export function explainLine(entry: ResolvedLine, cart: BoardCart): string[] {
  const { line, item, metrics } = entry;
  const supplier = getSupplier(cart.supplierId).shortName;
  const site = getSite(line.shipToId).name;
  const qty = (n: number) => fmtQty(n, item.uom);
  const phrase =
    line.demandRef === null
      ? null
      : demandRefPhrase(line.demandRef, line.needInDays);
  const bullets: string[] = [];
  if (line.forwardBuy) {
    bullets.push(
      `Added by you to buy ahead: ${qty(line.forwardBuy.addedQty)} covers ${line.forwardBuy.coverageDays} more days of ${item.code} at ${site}.`,
    );
    return bullets;
  }
  if (line.trigger === 'so' && phrase !== null) {
    bullets.push(
      `${phrase} is on back order. Open customer orders need ${qty(line.demand)} of ${item.code} and ${site} is short once stock on hand and incoming supply are counted.`,
    );
  } else if (line.trigger === 'bom' && phrase !== null) {
    bullets.push(
      `${phrase} needs ${qty(line.demand)} of ${item.code}, from the panel build plan. The need is reduced by stock on hand, open POs and safety stock, then moved earlier by the supplier's delivery time.`,
    );
  } else if (line.trigger === 'deficit') {
    bullets.push(
      `Expected demand of ${qty(line.demand)} over the planning period would take stock at ${site} below the safety stock of ${qty(line.safetyStock)}.`,
    );
  } else {
    bullets.push(
      `Stock on hand (${qty(line.onHand)}) at ${site} is below the minimum level, so the engine tops the item up to its maximum.`,
    );
  }
  bullets.push(
    `Still needed: demand ${qty(line.demand)} + safety stock ${qty(line.safetyStock)} − on hand ${qty(line.onHand)} − already on order ${qty(line.onOrder)} = ${qty(metrics.netRequirement)}.`,
  );
  if (line.plan === 'transfer' && line.transferFrom !== null) {
    bullets.push(
      `${getSite(line.transferFrom).name} has spare stock of this item, so the whole quantity is moved from there (${transferArrivalInDays(line.transferFrom, line.shipToId)} days in transit) and nothing is bought.`,
    );
  } else {
    const rounded = `Rounded up to ${qty(metrics.calcQty)} (the supplier sells in multiples of ${qty(item.packQty)}${metrics.calcQty === item.moq ? ', and this is its minimum order' : ''}).`;
    if (line.plan === 'buy_transfer' && line.transferFrom !== null) {
      bullets.push(
        `${qty(line.transferQty)} is moved from ${getSite(line.transferFrom).name}; the remaining ${qty(Math.max(0, metrics.netRequirement - line.transferQty))} is bought from ${supplier}. ${rounded}`,
      );
    } else {
      bullets.push(`Bought from ${supplier}. ${rounded}`);
    }
  }
  if (metrics.qty !== metrics.calcQty && line.plan !== 'transfer') {
    bullets.push(
      `Quantity adjusted from ${qty(metrics.calcQty)} to ${qty(metrics.qty)}.`,
    );
  }
  return [...bullets, ...line.why];
}

// ---------------------------------------------------------------------------
// Money formatting for notes
// ---------------------------------------------------------------------------

/** '$12.7K' for thousands, whole dollars below, '$2.17M' above a million. */
export function fmtMoneyShort(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `$${(abs / 1000).toFixed(1)}K`;
  return fmtMoney(n);
}

/** '45,000 ft' for wire (the data is in C), '40 each', '12 reel'. */
export function fmtQty(n: number, uom: string): string {
  return fmtQtyWithUnit(n, uom);
}

/** A unit price with what it is per: '$424.36 / 100 ft', '$5,500 / each'. */
export function fmtUnitPrice(price: number, uom: string): string {
  return `${fmtMoney(price, { cents: true })} / ${priceUnit(uom)}`;
}

/** The number alone, in display units: 450 C reads 45,000. */
export function fmtQtyNumber(n: number, uom: string): string {
  return fmtNumber(displayQty(n, uom));
}

/** What a cart's freight terms mean in plain words, with the agreement section. */
export function freightIncludedLabel(freight: {
  incoterm: string;
  clauseSection: string | null;
}): string {
  const what =
    freight.incoterm === 'DDP'
      ? 'Freight and duties included'
      : 'Freight included';
  return freight.clauseSection === null
    ? what
    : `${what} (agreement §${freight.clauseSection})`;
}

// ---------------------------------------------------------------------------
// Released POs
// ---------------------------------------------------------------------------

/** One-line status of a PO the board released, in the vocabulary of the PO pages. */
export function poHeadline(po: ReleasedPo): string {
  const supplier = getSupplier(po.supplierId).shortName;
  switch (po.status) {
    case 'Pending Approval': {
      const gate = pendingGate(po.gates);
      if (gate === null) return 'Pending approval';
      return gate.status === 'Escalated'
        ? `Pending approval (${gate.approverName}, escalated)`
        : `Pending approval (${gate.approverName})`;
    }
    case 'Approved': {
      const approved = [...po.gates]
        .reverse()
        .find((gate) => gate.status === 'Approved');
      return approved !== undefined && approved.decidedAt !== null
        ? `Approved by ${approved.approverName}`
        : 'Approved, within the buyer limit';
    }
    case 'Sent':
      return po.acknowledged
        ? 'Order confirmed by supplier in 2 h'
        : `Sent to ${supplier} electronically`;
    case 'Cancelled':
      return 'Rejected, returned to the buyer';
    default:
      return po.status;
  }
}

/** Lines the Auto-approve rule would approve (or has approved), for the toggle's popover. */
export function countAutoApproveEligible(
  carts: readonly ResolvedCart[],
  overrides: Readonly<Record<string, ApprovalState>>,
): { lines: number; carts: number } {
  let lines = 0;
  let cartCount = 0;
  for (const cart of carts) {
    const eligible = cart.lines.filter(
      (entry) =>
        entry.releasedPo === null &&
        entry.line.state === 'unapproved' &&
        overrides[entry.line.id] === undefined &&
        isAutoApproveEligible(entry.line, entry.item, entry.metrics),
    );
    lines += eligible.length;
    if (eligible.length > 0) cartCount += 1;
  }
  return { lines, carts: cartCount };
}
